/**
 * Ch99 Pseudocode Studio — one pseudocode per root-to-leaf trace.
 *
 * The unit of work is a whole chain of text, not a single node. "III/20/a"
 * says "...except products granted an exclusion by the USTR" — alone that is
 * not a rule. Read with III/20 (25% on products of China) and the note above
 * it, it is. So the chain is what gets interpreted, and the chain is what the
 * generator is handed.
 *
 * A chain's identity is the ordered hashes of its segments, so a chain
 * unchanged between revisions is one trace and one pseudocode; if an ancestor
 * is reworded the chain is genuinely new work, because its scope moved.
 */
import React, { useCallback, useEffect, useState } from 'react';

import {
  Alert, Box, Button, Chip, CircularProgress, Divider,
  IconButton, TextField, Tooltip, Typography,
} from '@mui/material';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';

import TopBar from '../../../../../components/utils/TopBar';
import * as api from '../../../../../api/index';
import { toast } from 'react-toastify';

const C = {
  bg: '#0a0e15', panel: '#171f2c', head: '#212c3d', sunk: '#0d131c',
  line: '#2d3a4d', border: '#425468', hover: '#1e2939', sel: '#173150',
  text: '#f2f6fa', dim: '#bac6d6', muted: '#8a9bb0',
  blue: '#7cc0ff', blueBg: '#152c44',
  retired: '#c79a5e', retiredBg: '#332616',
};

const STATUS = {
  PENDING:       { label: 'Pending',       color: '#9fb0c4', bg: '#222e3e', help: 'Not looked at yet' },
  PERFECT:       { label: 'Perfect',       color: '#5ce88f', bg: '#0f3526', help: 'Read, written and verified' },
  UNSURE:        { label: 'Unsure',        color: '#ffc93f', bg: '#382a0f', help: 'Written, but wants a second reader' },
  NO_PSEUDOCODE: { label: 'No pseudocode', color: '#a9aeff', bg: '#232348', help: 'Reviewed, and deliberately has none' },
};
const STATUS_ORDER = ['PENDING', 'PERFECT', 'UNSURE', 'NO_PSEUDOCODE'];
const MARKABLE = ['PERFECT', 'UNSURE', 'NO_PSEUDOCODE'];

// Each level of the chain gets its own tone, so depth reads at a glance.
const LEVEL_TONE = ['#7cc0ff', '#5ce88f', '#ffc93f', '#ff9f68', '#a9aeff'];

const inputSx = {
  '& .MuiInputBase-root': { color: C.text, background: C.sunk, fontSize: 13 },
  '& .MuiInputLabel-root': { color: C.dim, fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.muted },
};

const Panel = ({ title, right, children, pad = '14px', accent = C.blue, sx }) => (
  <Box sx={{
    background: C.panel, border: `1px solid ${C.line}`, borderRadius: '10px',
    overflow: 'hidden', minWidth: 0, boxShadow: '0 1px 3px rgba(0,0,0,.45)', ...sx,
  }}>
    <Box sx={{ px: '14px', py: '10px', background: C.head, borderBottom: `1px solid ${C.line}`,
      display: 'flex', alignItems: 'center', gap: '9px' }}>
      <Box sx={{ width: 3, height: 13, borderRadius: '2px', background: accent, flexShrink: 0 }} />
      <Typography variant="caption" fontWeight={700} color={C.text}
        sx={{ letterSpacing: '.07em', textTransform: 'uppercase', fontSize: 10.5 }}>{title}</Typography>
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>{right}</Box>
    </Box>
    <Box sx={{ p: pad }}>{children}</Box>
  </Box>
);

/** The chain as a path: III/20 › a › vi */
const TraceSpine = ({ path }) => {
  const parts = (path || '').split('/');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', flexWrap: 'wrap', minWidth: 0 }}>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Typography sx={{ fontSize: 11, color: C.muted }}>›</Typography>}
          <Typography sx={{
            fontSize: 12, fontFamily: 'monospace',
            fontWeight: i === parts.length - 1 ? 700 : 500,
            color: i === parts.length - 1 ? C.text : C.muted,
          }}>{p}</Typography>
        </React.Fragment>
      ))}
    </Box>
  );
};

const Ch99PseudocodeStudioPage = () => {
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState(null);
  const [work, setWork] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [building, setBuilding] = useState(false);

  const [statusFilter, setStatusFilter] = useState('');
  const [noteFilter, setNoteFilter] = useState('');
  const [scope, setScope] = useState('ALL');
  const [search, setSearch] = useState('');
  // LEVERAGE keeps the default "most placements first" ordering; PATH_ASC and
  // PATH_DESC walk the schedule's own order, level by level.
  const [sortMode, setSortMode] = useState('LEVERAGE');

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedSegs, setExpandedSegs] = useState(new Set());

  const [draft, setDraft] = useState('');
  const [draftStatus, setDraftStatus] = useState('PENDING');
  const [draftNote, setDraftNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadCoverage = useCallback(async () => {
    try {
      const { data, status } = await api.getCh99TraceCoverageBIZcoreTD();
      if (status === 200 && data?.data) setCoverage(data.data);
    } catch (e) { toast.error('Could not load coverage'); }
  }, []);

  const loadWorklist = useCallback(async () => {
    setListLoading(true);
    try {
      const q = new URLSearchParams({ limit: '500', scope });
      if (statusFilter) q.set('status', statusFilter);
      if (noteFilter) q.set('note', noteFilter);
      if (search) q.set('search', search);
      const { data, status } = await api.getCh99TraceWorklistBIZcoreTD(q.toString());
      if (status === 200 && data?.data) setWork(data.data);
    } catch (e) { toast.error('Could not load the worklist'); }
    setListLoading(false);
  }, [statusFilter, noteFilter, search, scope]);

  useEffect(() => {
    (async () => { await Promise.all([loadCoverage(), loadWorklist()]); setLoading(false); })();
  }, []); // eslint-disable-line
  useEffect(() => { if (!loading) loadWorklist(); }, [statusFilter, noteFilter, search, scope]); // eslint-disable-line

  const rebuild = async () => {
    setBuilding(true);
    try {
      const { data, status } = await api.buildCh99TracesBIZcoreTD();
      if (status === 201 && data?.data) {
        const d = data.data;
        toast.success(`${d.traces_total} traces · ${d.traces_created} new · ${d.reuse_factor}x reuse`);
        await Promise.all([loadCoverage(), loadWorklist()]);
      }
    } catch (e) { toast.error('Could not rebuild traces'); }
    setBuilding(false);
  };

  const applyDetail = (d) => {
    setDetail(d);
    setDraft(d.pseudocode?.pseudocode_text || '');
    setDraftStatus(d.pseudocode?.review_status || (d.is_trivial ? 'NO_PSEUDOCODE' : 'PENDING'));
    setDraftNote(d.pseudocode?.reviewer_note || '');
    setDirty(false);
    // The leaf opens by default — it is the part being interpreted; its scope
    // sits above it and is read on demand.
    setExpandedSegs(new Set([d.segments.length - 1]));
  };

  const select = async (traceId) => {
    if (dirty && !window.confirm('Discard unsaved pseudocode?')) return;
    setSelectedId(traceId); setDetailLoading(true); setShowHistory(false);
    try {
      const { data, status } = await api.getCh99TraceDetailBIZcoreTD(traceId);
      if (status === 200 && data?.data) applyDetail(data.data);
    } catch (e) { toast.error('Could not load that trace'); }
    setDetailLoading(false);
  };

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { data, status } = await api.saveCh99TracePseudocodeBIZcoreTD(detail.trace_id, {
        pseudocode_text: draft, review_status: draftStatus, reviewer_note: draftNote,
      });
      if ((status === 200 || status === 202) && data?.data) {
        applyDetail(data.data); toast.success('Saved');
        loadCoverage(); loadWorklist();
      } else toast.error(typeof data?.data === 'string' ? data.data : 'Could not save');
    } catch (e) { toast.error(e?.response?.data?.data || 'Could not save'); }
    setSaving(false);
  };

  const generate = async () => {
    if (!detail) return;
    setGenerating(true);
    try {
      const { data, status } = await api.generateCh99TracePseudocodeBIZcoreTD({
        trace_id: detail.trace_id, provider: 'MOCK', auto_accept: false,
      });
      if (status === 201 && data?.data) {
        if (data.data.result === 'SUCCESS') {
          setDraft(data.data.pseudocode_text);
          setDraftStatus(detail.is_trivial ? 'NO_PSEUDOCODE' : 'UNSURE');
          setDirty(true);
          toast.info(`Read ${data.data.request_chars.toLocaleString()} chars across ${detail.segment_count} segments in ${data.data.latency_ms}ms`);
          setDetail({ ...detail, generations: data.data.detail.generations });
        } else toast.error(data.data.error || 'Generation failed');
      }
    } catch (e) { toast.error(e?.response?.data?.data || 'Generation failed'); }
    setGenerating(false);
  };

  const toggleSeg = (i) => setExpandedSegs((prev) => {
    const n = new Set(prev);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  // The backend ships a sort_key — one integer per hierarchy level — because
  // none of the levels sort lexically: note 20 follows note 3, subdivision
  // (aa) follows (z), subprovision (ix) follows (viii).
  const compareKeys = (a, b) => {
    const ka = a.sort_key || [], kb = b.sort_key || [];
    for (let i = 0; i < Math.max(ka.length, kb.length); i += 1) {
      const d = (ka[i] ?? -1) - (kb[i] ?? -1);
      if (d !== 0) return d;
    }
    return 0;
  };

  const visibleRows = React.useMemo(() => {
    const rows = [...(work?.rows || [])];
    if (sortMode === 'PATH_ASC') return rows.sort(compareKeys);
    if (sortMode === 'PATH_DESC') return rows.sort((a, b) => compareKeys(b, a));
    return rows;                      // server order: leverage
  }, [work, sortMode]);

  const cov = coverage;

  if (loading) return (<Box><TopBar title={"Ch99 Pseudocode Studio"} />
    <Box height="300px" className="flexCenterCenterRow"><CircularProgress /></Box></Box>);

  if (!cov || cov.total_traces === 0) {
    return (<Box><TopBar title={"Ch99 Pseudocode Studio"} />
      <Box sx={{ p: '20px' }}>
        <Alert severity="info" sx={{ mb: '14px' }}>
          No traces built yet. A trace is one root-to-leaf chain of Subchapter III —
          the leaf plus every heading above it. Build them from the ingested substrate to begin.
        </Alert>
        <Button variant="contained" disableElevation onClick={rebuild} disabled={building}
          startIcon={building ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AccountTreeOutlinedIcon />}
          sx={{ textTransform: 'none' }}>Build traces</Button>
      </Box></Box>);
  }

  return (
    <Box>
      <TopBar title={"Ch99 Pseudocode Studio"} />
      <Box sx={{ background: C.bg, borderRadius: '8px', border: `1px solid ${C.line}`, p: '16px' }}>

        {/* ── Coverage ─────────────────────────────────────────────────── */}
        <Panel title="Subchapter III coverage" right={
          <>
            <Tooltip title="A chain unchanged between revisions is one trace and one pseudocode. This is how many leaf placements each interpretation covers.">
              <Typography variant="caption" color={C.dim} sx={{ fontSize: 10.5 }}>
                {cov.total_traces} traces cover {cov.total_occurrences} placements · {cov.reuse_factor}x reuse
              </Typography>
            </Tooltip>
            <Tooltip title="Rebuild from the substrate. Safe to re-run — pseudocode keys to the chain hash and survives.">
              <IconButton size="small" onClick={rebuild} disabled={building}
                sx={{ color: building ? C.muted : C.blue, p: '2px' }}>
                {building ? <CircularProgress size={14} /> : <RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </>
        }>
          <Box sx={{ display: 'flex', height: 16, borderRadius: '99px', overflow: 'hidden',
            background: C.sunk, border: `1px solid ${C.line}`, mb: '14px' }}>
            {STATUS_ORDER.map((k) => {
              const n = cov.status_counts[k] || 0;
              if (!n) return null;
              return <Tooltip key={k} title={`${STATUS[k].label}: ${n}`}><Box sx={{
                width: `${(n / cov.total_traces) * 100}%`, background: STATUS[k].color,
                opacity: k === 'PENDING' ? 0.4 : 1 }} /></Tooltip>;
            })}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: '10px' }}>
            {STATUS_ORDER.map((k) => (
              <Box key={k} onClick={() => setStatusFilter(statusFilter === k ? '' : k)}
                sx={{ p: '12px 14px', borderRadius: '8px', cursor: 'pointer', background: STATUS[k].bg,
                  border: `1px solid ${C.line}`, borderTop: `3px solid ${STATUS[k].color}`,
                  outline: statusFilter === k ? `1px solid ${STATUS[k].color}` : 'none',
                  '&:hover': { borderColor: STATUS[k].color } }}>
                <Typography sx={{ fontSize: 25, fontWeight: 700, color: STATUS[k].color,
                  fontFamily: 'monospace', lineHeight: 1.15 }}>{cov.status_counts[k] || 0}</Typography>
                <Typography variant="caption" color={C.dim} sx={{ fontSize: 11.5, fontWeight: 500 }}>{STATUS[k].label}</Typography>
              </Box>
            ))}
            <Box sx={{ p: '12px 14px', borderRadius: '8px', background: C.blueBg,
              border: `1px solid ${C.line}`, borderTop: `3px solid ${C.blue}` }}>
              <Typography sx={{ fontSize: 25, fontWeight: 700, color: C.blue, fontFamily: 'monospace', lineHeight: 1.15 }}>
                {cov.settled_pct}%</Typography>
              <Typography variant="caption" color={C.dim} sx={{ fontSize: 11.5, fontWeight: 500 }}>Settled</Typography>
            </Box>
            <Box onClick={() => setScope(scope === 'RETIRED' ? 'ALL' : 'RETIRED')}
              sx={{ p: '12px 14px', borderRadius: '8px', cursor: 'pointer', background: C.retiredBg,
                border: `1px solid ${scope === 'RETIRED' ? C.retired : C.line}`,
                borderTop: `3px solid ${C.retired}` }}>
              <Typography sx={{ fontSize: 25, fontWeight: 700, color: C.retired, fontFamily: 'monospace', lineHeight: 1.15 }}>
                {cov.retired_traces}</Typography>
              <Tooltip title="Chains the current edition no longer carries. They still govern entries dated inside their window, so they still need interpreting.">
                <Typography variant="caption" color={C.dim} sx={{ fontSize: 11.5, fontWeight: 500 }}>Retired</Typography>
              </Tooltip>
            </Box>
          </Box>
        </Panel>

        {/* ── Per-revision ─────────────────────────────────────────────── */}
        {cov.revisions?.length > 0 && (
          <Box sx={{ mt: '14px' }}>
            <Panel title="Coverage by revision" accent={C.retired}
              right={<Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
                click to show only that edition's chains</Typography>}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))', gap: '10px' }}>
                {cov.revisions.slice().reverse().map((r) => (
                  <Box key={r.revision_id}
                    onClick={() => setScope(scope === r.revision_id ? 'ALL' : r.revision_id)}
                    sx={{ p: '11px 13px', borderRadius: '8px', background: C.sunk, cursor: 'pointer',
                      border: `1px solid ${scope === r.revision_id || r.is_latest ? C.blue : C.line}`,
                      '&:hover': { borderColor: C.blue } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '7px' }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>
                        {r.revision_name}</Typography>
                      {r.is_latest && <Chip label="current" size="small" sx={{ height: 15, fontSize: 9,
                        fontWeight: 700, background: C.blueBg, color: C.blue, border: 'none',
                        '& .MuiChip-label': { px: '5px' } }} />}
                      <Typography variant="caption" sx={{ fontSize: 11, ml: 'auto', fontFamily: 'monospace',
                        fontWeight: 700, color: r.settled_pct === 100 ? STATUS.PERFECT.color : C.dim }}>
                        {r.settled_pct}%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', height: 7, borderRadius: '99px', overflow: 'hidden',
                      background: C.bg, border: `1px solid ${C.line}`, mb: '7px' }}>
                      {STATUS_ORDER.map((k) => {
                        const n = r.status_counts[k] || 0;
                        if (!n) return null;
                        return <Box key={k} sx={{ width: `${(n / r.traces) * 100}%`,
                          background: STATUS[k].color, opacity: k === 'PENDING' ? 0.4 : 1 }} />;
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>{r.traces} traces</Typography>
                      <Typography variant="caption" sx={{ fontSize: 10.5, color: r.pending ? STATUS.UNSURE.color : C.muted }}>
                        {r.pending} pending</Typography>
                      {r.retired_traces > 0 && (
                        <Tooltip title={`${r.retired_traces} chain(s) this revision carries that the current edition dropped — ${r.retired_pending} still uninterpreted.`}>
                          <Typography variant="caption" sx={{ fontSize: 10.5, color: C.retired, ml: 'auto', fontWeight: 600 }}>
                            {r.retired_pending}/{r.retired_traces} retired gaps</Typography>
                        </Tooltip>)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Panel>
          </Box>
        )}

        {/* ── Notes ────────────────────────────────────────────────────── */}
        <Box sx={{ mt: '14px' }}>
          <Panel title="By note" pad="10px"
            right={<Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>click to filter</Typography>}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(cov.by_note || []).map((n) => {
                const on = noteFilter === n.note;
                const done = n.settled_pct === 100;
                return (
                  <Tooltip key={n.note} placement="top"
                    title={`${n.total} traces · ${n.settled_pct}% settled · ${n.retired} retired`}>
                    <Box onClick={() => setNoteFilter(on ? '' : n.note)}
                      sx={{ px: '10px', py: '6px', borderRadius: '6px', cursor: 'pointer',
                        background: on ? C.sel : (done ? STATUS.PERFECT.bg : C.sunk),
                        border: `1px solid ${on ? C.blue : (done ? `${STATUS.PERFECT.color}66` : C.line)}`,
                        display: 'flex', alignItems: 'center', gap: '8px',
                        '&:hover': { borderColor: C.blue } }}>
                      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: C.text }}>{n.note}</Typography>
                      <Box sx={{ width: 34, height: 5, borderRadius: '99px', background: C.bg,
                        border: `1px solid ${C.line}`, overflow: 'hidden' }}>
                        <Box sx={{ width: `${n.settled_pct}%`, height: '100%',
                          background: done ? STATUS.PERFECT.color : C.blue }} /></Box>
                      <Typography variant="caption" color={C.dim} sx={{ fontSize: 10.5, fontWeight: 600 }}>{n.total}</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Panel>
        </Box>

        {/* ── Queue + chain ────────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(330px, 440px) 1fr', gap: '14px', mt: '14px', alignItems: 'start' }}>

          <Panel title={`Traces${noteFilter ? ` · note ${noteFilter}` : ''}${scope === 'RETIRED' ? ' · retired' : ''}`} pad="0"
            right={
              <>
                <Box sx={{ display: 'flex', gap: '2px', mr: '2px' }}>
                  {[
                    { k: 'LEVERAGE',  icon: <SwapVertOutlinedIcon sx={{ fontSize: 14 }} />,
                      tip: 'Most placements first — the chains worth settling soonest' },
                    { k: 'PATH_ASC',  icon: <ArrowUpwardIcon sx={{ fontSize: 14 }} />,
                      tip: 'Schedule order, ascending — note 3 before note 20, (z) before (aa), (ix) before (x)' },
                    { k: 'PATH_DESC', icon: <ArrowDownwardIcon sx={{ fontSize: 14 }} />,
                      tip: 'Schedule order, descending' },
                  ].map((o) => (
                    <Tooltip key={o.k} title={o.tip}>
                      <IconButton size="small" onClick={() => setSortMode(o.k)}
                        sx={{ p: '3px', borderRadius: '5px',
                          color: sortMode === o.k ? C.blue : C.muted,
                          background: sortMode === o.k ? C.blueBg : 'transparent',
                          border: `1px solid ${sortMode === o.k ? C.blue : 'transparent'}` }}>
                        {o.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>
                <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5 }}>
                  {listLoading ? '…' : `${work?.total || 0}`}</Typography>
              </>
            }>
            <Box sx={{ p: '10px 12px', borderBottom: `1px solid ${C.line}` }}>
              <TextField fullWidth size="small" placeholder="Search path, hash or text…"
                value={search} onChange={(e) => setSearch(e.target.value)} sx={inputSx} />
            </Box>
            <Box sx={{ maxHeight: 640, overflow: 'auto' }}>
              {visibleRows.map((r) => {
                const on = selectedId === r.trace_id;
                const st = STATUS[r.review_status] || STATUS.PENDING;
                return (
                  <Box key={r.trace_id} onClick={() => select(r.trace_id)}
                    sx={{ px: '12px', py: '9px', cursor: 'pointer', borderBottom: `1px solid ${C.line}`,
                      borderLeft: `3px solid ${on ? C.blue : (r.review_status === 'PENDING' ? 'transparent' : st.color)}`,
                      background: on ? C.sel : 'transparent',
                      opacity: r.is_current ? 1 : 0.72,
                      '&:hover': { background: on ? C.sel : C.hover } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '4px' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}><TraceSpine path={r.leaf_label_path} /></Box>
                      {!r.is_current && (
                        <Chip label="retired" size="small" sx={{ height: 15, fontSize: 9, fontWeight: 700,
                          background: C.retiredBg, color: C.retired, border: 'none', '& .MuiChip-label': { px: '5px' } }} />)}
                      <Chip label={st.label} size="small" sx={{ height: 16, fontSize: 9.5, fontWeight: 700,
                        background: st.bg, color: st.color, border: `1px solid ${st.color}66`,
                        '& .MuiChip-label': { px: '6px' } }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <Tooltip title={`${r.segment_count} segments read together`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <AccountTreeOutlinedIcon sx={{ fontSize: 12, color: C.muted }} />
                          <Typography variant="caption" color={C.dim} sx={{ fontSize: 10.5 }}>{r.segment_count}</Typography>
                        </Box></Tooltip>
                      <Tooltip title={`Appears in ${r.occurrences} leaf placement(s): ${r.revisions.join(', ')}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <LayersOutlinedIcon sx={{ fontSize: 12, color: r.occurrences > 1 ? C.blue : C.muted }} />
                          <Typography variant="caption" sx={{ fontSize: 10.5, color: r.occurrences > 1 ? C.blue : C.muted }}>
                            {r.occurrences}</Typography>
                        </Box></Tooltip>
                      <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5, fontFamily: 'monospace', ml: 'auto' }}>
                        {r.combined_char_count.toLocaleString()} ch</Typography>
                      {r.is_trivial && <Chip label="tiny" size="small" sx={{ height: 15, fontSize: 9.5,
                        background: STATUS.NO_PSEUDOCODE.bg, color: STATUS.NO_PSEUDOCODE.color, border: 'none',
                        '& .MuiChip-label': { px: '5px' } }} />}
                    </Box>
                  </Box>
                );
              })}
              {!listLoading && visibleRows.length === 0 && (
                <Box sx={{ p: '28px', textAlign: 'center' }}>
                  <Typography variant="body2" color={C.muted} sx={{ fontSize: 13 }}>Nothing matches these filters.</Typography>
                </Box>)}
            </Box>
          </Panel>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
            {!detail ? (
              <Panel title="The chain">
                <Typography variant="body2" color={C.muted} sx={{ fontSize: 13, lineHeight: 1.75 }}>
                  Pick a trace. Each one is a whole root-to-leaf chain — the leaf plus every
                  heading above it — and that chain is what gets interpreted and what the
                  generator is handed. The queue leads with the chains covering the most placements.
                </Typography>
              </Panel>
            ) : detailLoading ? (
              <Panel title="The chain"><Box className="flexCenterCenterRow" height="180px"><CircularProgress size={22} /></Box></Panel>
            ) : (
              <>
                <Panel
                  accent={detail.is_current ? C.blue : C.retired}
                  title="The chain — read top to bottom"
                  right={
                    <>
                      <Typography variant="caption" color={C.dim} sx={{ fontSize: 10.5, fontFamily: 'monospace' }}>
                        {detail.segment_count} segments · {detail.combined_char_count.toLocaleString()} ch · {detail.trace_hash?.substring(0, 10)}
                      </Typography>
                      <Tooltip title={expandedSegs.size === detail.segments.length ? 'Collapse all' : 'Expand all'}>
                        <IconButton size="small" sx={{ color: C.muted, p: '2px' }}
                          onClick={() => setExpandedSegs(expandedSegs.size === detail.segments.length
                            ? new Set() : new Set(detail.segments.map((_, i) => i)))}>
                          {expandedSegs.size === detail.segments.length
                            ? <UnfoldLessOutlinedIcon sx={{ fontSize: 16 }} />
                            : <UnfoldMoreOutlinedIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    </>
                  }>
                  {!detail.is_current && (
                    <Alert severity="warning" sx={{ mb: '12px', fontSize: 12.5 }}>
                      This chain is <strong>not in {detail.latest_revision}</strong>. It still needs a
                      pseudocode — entries dated inside its window resolve against it.
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '12px' }}>
                    {detail.occurrences.map((o, i) => (
                      <Chip key={i} size="small" label={`${o.revision_name} · ${o.leaf_label_path}`} sx={{
                        height: 20, fontSize: 10.5, fontFamily: 'monospace',
                        background: C.head, color: C.dim, border: `1px solid ${C.border}`,
                        '& .MuiChip-label': { px: '8px' } }} />
                    ))}
                  </Box>

                  {detail.segments.map((s, i) => {
                    const open = expandedSegs.has(i);
                    const tone = LEVEL_TONE[Math.min(i, LEVEL_TONE.length - 1)];
                    return (
                      <Box key={i} sx={{ display: 'flex', gap: '10px', mb: '8px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, pt: '3px' }}>
                          <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: tone,
                            border: `2px solid ${C.panel}`, boxShadow: `0 0 0 1.5px ${tone}` }} />
                          {i < detail.segments.length - 1 && (
                            <Box sx={{ width: 2, flex: 1, minHeight: 18, background: `${tone}55`, mt: '3px' }} />)}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, pb: '4px' }}>
                          <Box onClick={() => toggleSeg(i)}
                            sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                              p: '7px 10px', borderRadius: '6px',
                              background: s.is_leaf ? C.sel : C.sunk,
                              border: `1px solid ${s.is_leaf ? C.blue : C.line}`,
                              '&:hover': { borderColor: tone } }}>
                            <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: C.text }}>
                              {s.node_label_path}</Typography>
                            <Chip label={s.node_type} size="small" sx={{ height: 16, fontSize: 9,
                              background: 'transparent', color: tone, border: `1px solid ${tone}77`,
                              '& .MuiChip-label': { px: '6px' } }} />
                            {s.is_leaf && <Chip label="leaf" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700,
                              background: C.blueBg, color: C.blue, border: 'none', '& .MuiChip-label': { px: '6px' } }} />}
                            <Typography variant="caption" color={C.muted}
                              sx={{ fontSize: 10.5, fontFamily: 'monospace', ml: 'auto' }}>
                              {s.char_count.toLocaleString()} ch</Typography>
                          </Box>
                          {open && (
                            <Box sx={{ mt: '6px', p: '11px 13px', borderRadius: '6px', background: C.sunk,
                              border: `1px solid ${C.line}`, borderLeft: `3px solid ${tone}`,
                              fontFamily: 'monospace', fontSize: 12.2, lineHeight: 1.8, color: C.text,
                              maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {s.raw_legal_text || '(no text of its own)'}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                  {detail.is_trivial && (
                    <Alert severity="info" sx={{ mt: '8px', fontSize: 12.5 }}>
                      This whole chain is under 200 characters. If it states no rule, mark it
                      <strong> No pseudocode</strong> rather than inventing one.
                    </Alert>
                  )}
                </Panel>

                <Panel title="Pseudocode for this chain"
                  right={
                    <>
                      {detail.pseudocode?.reviewed_by && (
                        <Typography variant="caption" color={C.muted} sx={{ fontSize: 10 }}>
                          {detail.pseudocode.reviewed_by} · {String(detail.pseudocode.reviewed_at || '').substring(0, 10)}
                        </Typography>)}
                      {detail.generations?.length > 0 && (
                        <Tooltip title="Generation history">
                          <IconButton size="small" onClick={() => setShowHistory(!showHistory)}
                            sx={{ color: showHistory ? C.blue : C.muted, p: '2px' }}>
                            <HistoryOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>)}
                    </>
                  }>
                  <Box sx={{ display: 'flex', gap: '8px', mb: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {MARKABLE.map((k) => (
                      <Tooltip key={k} title={STATUS[k].help} placement="top">
                        <Chip label={STATUS[k].label} size="small"
                          onClick={() => { setDraftStatus(k); setDirty(true); }}
                          sx={{ height: 26, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                            background: draftStatus === k ? STATUS[k].bg : 'transparent',
                            color: draftStatus === k ? STATUS[k].color : C.muted,
                            border: `1px solid ${draftStatus === k ? STATUS[k].color : C.border}`,
                            '& .MuiChip-label': { px: '10px' } }} />
                      </Tooltip>
                    ))}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: '8px' }}>
                      <Tooltip title={`Sends the whole ${detail.combined_char_count.toLocaleString()}-character chain, not just the leaf`}>
                        <span><Button size="small" variant="outlined" disabled={generating} onClick={generate}
                          startIcon={generating ? <CircularProgress size={13} /> : <AutoAwesomeOutlinedIcon sx={{ fontSize: 15 }} />}
                          sx={{ textTransform: 'none', fontSize: 12.5, color: C.blue, borderColor: C.border }}>
                          {generating ? 'Reading chain…' : 'Generate from chain'}</Button></span>
                      </Tooltip>
                      <Button size="small" variant="contained" disableElevation disabled={saving || !dirty} onClick={save}
                        startIcon={saving ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <SaveOutlinedIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: 'none', fontSize: 12.5 }}>Save</Button>
                    </Box>
                  </Box>

                  {draftStatus === 'NO_PSEUDOCODE' ? (
                    <Alert severity="info" sx={{ fontSize: 12.5, mb: '10px' }}>
                      Saves with an empty body — either this chain carries no rule, or its
                      meaning could not be determined. Leave a note saying which.
                    </Alert>
                  ) : (
                    <TextField fullWidth multiline minRows={12} value={draft}
                      onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
                      placeholder="What do these segments say, read together, as pseudocode?"
                      sx={{ ...inputSx, mb: '10px',
                        '& .MuiInputBase-root': { ...inputSx['& .MuiInputBase-root'],
                          fontFamily: 'monospace', fontSize: 12.2, lineHeight: 1.7 } }} />
                  )}

                  <TextField fullWidth size="small" value={draftNote} label="Reviewer note"
                    onChange={(e) => { setDraftNote(e.target.value); setDirty(true); }}
                    placeholder="Why unsure, or which reason for no pseudocode?" sx={inputSx} />

                  {dirty && <Typography variant="caption" sx={{ fontSize: 11, color: STATUS.UNSURE.color, mt: '8px', display: 'block' }}>
                    Unsaved changes</Typography>}

                  {showHistory && detail.generations?.length > 0 && (
                    <>
                      <Divider sx={{ borderColor: C.line, my: '12px' }} />
                      <Typography variant="caption" color={C.dim} sx={{ fontSize: 10.5, letterSpacing: '.07em', fontWeight: 700 }}>
                        GENERATION ATTEMPTS</Typography>
                      {detail.generations.map((g) => (
                        <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '6px',
                          borderBottom: `1px solid ${C.line}` }}>
                          <Chip size="small" label={g.provider} sx={{ height: 16, fontSize: 9, background: C.head,
                            color: C.dim, border: `1px solid ${C.line}`, '& .MuiChip-label': { px: '5px' } }} />
                          <Typography variant="caption" sx={{ fontSize: 10.5,
                            color: g.result === 'SUCCESS' ? STATUS.PERFECT.color : '#ff8f8f' }}>{g.result}</Typography>
                          <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5, flex: 1, minWidth: 0 }}>
                            {String(g.created_at).substring(0, 19).replace('T', ' ')}
                            {g.accepted && <span style={{ color: C.blue }}> · accepted</span>}</Typography>
                          <Typography variant="caption" color={C.muted} sx={{ fontSize: 10.5, fontFamily: 'monospace' }}>
                            {g.latency_ms}ms</Typography>
                          <Button size="small" onClick={() => { setDraft(g.pseudocode_text || ''); setDirty(true); }}
                            sx={{ textTransform: 'none', fontSize: 11, color: C.blue, minWidth: 0 }}>Load</Button>
                        </Box>
                      ))}
                    </>
                  )}
                </Panel>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Ch99PseudocodeStudioPage;
